import { useEffect, useMemo, useRef, useState } from 'react';
import { X, CaretLeft, CaretRight, ImageBroken, Images } from '@phosphor-icons/react';
import { MEDIA_CATEGORIES, type HotelMediaItem } from '@pms/shared';

const CATEGORY_LABEL = new Map(MEDIA_CATEGORIES.map((c) => [c.key, c.label]));

interface Photo {
  url: string;
  category: string | null;
}

interface HotelGalleryProps {
  images: string[];
  /** Categorised media (TASK-114); preferred over `images` when present. */
  gallery?: HotelMediaItem[] | undefined;
  name: string;
}

/**
 * Gallery: a 5-up collage with a "View all photos" button, category filter chips,
 * and a focus-trapped lightbox (‹ › + keyboard) that can be scoped to a category.
 */
export function HotelGallery({ images, gallery, name }: HotelGalleryProps) {
  const photos: Photo[] = useMemo(() => {
    const imgs = (gallery ?? []).filter((m) => m.type === 'image');
    if (imgs.length) return imgs.map((m) => ({ url: m.url, category: m.category }));
    return images.map((url) => ({ url, category: null }));
  }, [gallery, images]);

  // category key (null = all) currently scoping the lightbox
  const [filter, setFilter] = useState<string | null>(null);
  const [lightbox, setLightbox] = useState<number | null>(null);

  const chips = useMemo(() => {
    const counts = new Map<string, number>();
    for (const p of photos) if (p.category) counts.set(p.category, (counts.get(p.category) ?? 0) + 1);
    return MEDIA_CATEGORIES.filter((c) => counts.has(c.key)).map((c) => ({
      key: c.key,
      label: c.label,
      count: counts.get(c.key)!,
    }));
  }, [photos]);

  const filtered = useMemo(
    () => (filter ? photos.filter((p) => p.category === filter) : photos),
    [photos, filter],
  );

  if (photos.length === 0) {
    return (
      <div className="flex aspect-[16/9] w-full items-center justify-center rounded-2xl bg-clay-light text-clay">
        <ImageBroken className="h-10 w-10" aria-hidden="true" />
        <span className="sr-only">No photos available</span>
      </div>
    );
  }

  function open(cat: string | null, index: number) {
    setFilter(cat);
    setLightbox(index);
  }

  return (
    <>
      <div className="relative grid grid-cols-4 gap-2 overflow-hidden rounded-2xl">
        <button
          type="button"
          onClick={() => open(null, 0)}
          className="col-span-4 sm:col-span-2 sm:row-span-2"
          aria-label={`Open ${name} photo 1`}
        >
          <Img src={photos[0]!.url} alt={`${name} – photo 1`} className="aspect-[4/3] sm:h-full" />
        </button>
        {photos.slice(1, 5).map((p, i) => (
          <button
            key={p.url + i}
            type="button"
            onClick={() => open(null, i + 1)}
            className="group relative hidden sm:block"
            aria-label={`Open ${name} photo ${i + 2}`}
          >
            <Img src={p.url} alt={`${name} – photo ${i + 2}`} className="aspect-[4/3]" />
            {/* "View all" affordance on the bottom-right tile */}
            {i === 3 && photos.length > 5 && (
              <span className="absolute inset-0 flex items-center justify-center bg-ink/55 text-sm font-semibold text-white transition group-hover:bg-ink/65">
                <span className="inline-flex items-center gap-1.5 rounded-full bg-white/95 px-4 py-2 text-ink">
                  <Images className="h-4 w-4" aria-hidden="true" /> View all {photos.length} photos
                </span>
              </span>
            )}
          </button>
        ))}
      </div>

      {/* Mobile: explicit "view all" since the side tiles are hidden */}
      <button
        type="button"
        onClick={() => open(null, 0)}
        className="mt-3 inline-flex items-center gap-1.5 rounded-full border border-clay px-4 py-2 text-sm font-semibold text-clay sm:hidden"
      >
        <Images className="h-4 w-4" aria-hidden="true" /> View all {photos.length} photos
      </button>

      {/* Category chips — browse photos by what's in them */}
      {chips.length > 0 && (
        <div className="mt-3 flex flex-wrap gap-2">
          <Chip active={false} onClick={() => open(null, 0)}>
            All photos ({photos.length})
          </Chip>
          {chips.map((c) => (
            <Chip key={c.key} active={false} onClick={() => open(c.key, 0)}>
              {c.label} ({c.count})
            </Chip>
          ))}
        </div>
      )}

      {lightbox !== null && (
        <Lightbox
          items={filtered}
          index={Math.min(lightbox, filtered.length - 1)}
          name={name}
          chips={chips}
          filter={filter}
          onFilter={(k) => {
            setFilter(k);
            setLightbox(0);
          }}
          onClose={() => setLightbox(null)}
          onIndex={setLightbox}
        />
      )}
    </>
  );
}

function Chip({
  active,
  onClick,
  children,
}: {
  active: boolean;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`rounded-full border px-3.5 py-1.5 text-sm font-semibold transition focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-clay ${
        active
          ? 'border-clay bg-clay text-white'
          : 'border-sand bg-white text-ink hover:border-clay hover:text-clay'
      }`}
    >
      {children}
    </button>
  );
}

function Lightbox({
  items,
  index,
  name,
  chips,
  filter,
  onFilter,
  onClose,
  onIndex,
}: {
  items: Photo[];
  index: number;
  name: string;
  chips: { key: string; label: string; count: number }[];
  filter: string | null;
  onFilter: (key: string | null) => void;
  onClose: () => void;
  onIndex: (i: number) => void;
}) {
  const closeRef = useRef<HTMLButtonElement>(null);
  const count = items.length;
  useEffect(() => {
    closeRef.current?.focus();
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') onClose();
      else if (e.key === 'ArrowRight') onIndex((index + 1) % count);
      else if (e.key === 'ArrowLeft') onIndex((index - 1 + count) % count);
    }
    document.addEventListener('keydown', onKey);
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = prev;
    };
  }, [index, count, onClose, onIndex]);

  const current = items[index];
  const catLabel = current?.category ? CATEGORY_LABEL.get(current.category) : undefined;

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label={`${name} photos`}
      className="fixed inset-0 z-50 flex flex-col bg-black/90 p-4"
    >
      <div className="flex items-center justify-between gap-3">
        <p className="text-sm font-medium text-white/90">
          {name} — {filter ? CATEGORY_LABEL.get(filter) : 'All photos'}
        </p>
        <button
          ref={closeRef}
          type="button"
          onClick={onClose}
          aria-label="Close gallery"
          className="flex h-11 w-11 items-center justify-center rounded-full bg-white/10 text-white hover:bg-white/20 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white"
        >
          <X className="h-6 w-6" />
        </button>
      </div>

      {/* Category filter inside the lightbox */}
      {chips.length > 0 && (
        <div className="mt-3 flex flex-wrap gap-2">
          <LbChip active={filter === null} onClick={() => onFilter(null)}>
            All
          </LbChip>
          {chips.map((c) => (
            <LbChip key={c.key} active={filter === c.key} onClick={() => onFilter(c.key)}>
              {c.label} ({c.count})
            </LbChip>
          ))}
        </div>
      )}

      <div className="relative flex flex-1 items-center justify-center overflow-hidden">
        <button
          type="button"
          aria-label="Previous photo"
          onClick={() => onIndex((index - 1 + count) % count)}
          className="absolute left-2 flex h-11 w-11 items-center justify-center rounded-full bg-white/10 text-white hover:bg-white/20"
        >
          <CaretLeft className="h-6 w-6" />
        </button>
        {current && (
          <img
            src={current.url}
            alt={`${name} – photo ${index + 1}`}
            className="max-h-full max-w-full rounded-lg object-contain"
          />
        )}
        <button
          type="button"
          aria-label="Next photo"
          onClick={() => onIndex((index + 1) % count)}
          className="absolute right-2 flex h-11 w-11 items-center justify-center rounded-full bg-white/10 text-white hover:bg-white/20"
        >
          <CaretRight className="h-6 w-6" />
        </button>
      </div>

      <p className="mt-2 text-center text-sm text-white/80">
        {index + 1} / {count}
        {catLabel ? ` · ${catLabel}` : ''}
      </p>
    </div>
  );
}

function LbChip({
  active,
  onClick,
  children,
}: {
  active: boolean;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`rounded-full px-3 py-1 text-xs font-semibold transition ${
        active ? 'bg-white text-ink' : 'bg-white/15 text-white hover:bg-white/25'
      }`}
    >
      {children}
    </button>
  );
}

function Img({ src, alt, className }: { src: string; alt: string; className?: string }) {
  const [err, setErr] = useState(false);
  if (err) {
    return (
      <div className={`flex w-full items-center justify-center bg-clay-light text-clay ${className ?? ''}`}>
        <ImageBroken className="h-8 w-8" aria-hidden="true" />
      </div>
    );
  }
  return (
    <img
      src={src}
      alt={alt}
      loading="lazy"
      decoding="async"
      onError={() => setErr(true)}
      className={`w-full object-cover ${className ?? ''}`}
    />
  );
}
